/**
 * Retrieves the value of a cookie by its name.
 *
 * @param name - The name of the cookie.
 * @returns The decoded value of the cookie, or undefined if the cookie does not exist.
 */
export function getCookie(name: string): string | undefined {
  const cookieName = `${encodeURIComponent(name.trim())}=`;
  const cookies = document.cookie.split(";");
  const cookie = cookies.find((cookie) => cookie.trim().startsWith(cookieName));
  if (cookie) {
    const raw = cookie.split("=")[1];
    try {
      return decodeURIComponent(raw);
    } catch {
      // Narrow migration fallback: only for legacy escape() %uXXXX sequences
      // (e.g. escape("€") === "%u20AC") that decodeURIComponent does not accept.
      // Other malformed percent-encoding is not a known migration case and returns
      // undefined (same as cookie-not-found), rather than silently masking errors.
      // This fallback can be removed once all pre-migration cookies have expired
      // (cookie max-age is 180 days).
      if (/%u[0-9A-Fa-f]{4}/i.test(raw)) {
        return unescape(raw);
      }
    }
  }
}

/**
 * Sets a cookie with the specified name, value, and expiration days.
 *
 * @param name - The name of the cookie.
 * @param value - The value to be stored in the cookie.
 * @param exdays - The number of days until the cookie expires. Defaults to 7.
 * @returns void
 */
export function setCookie(name: string, value: string, exdays = 7): void {
  const exdate = new Date();
  exdate.setDate(exdate.getDate() + exdays);
  const cookieName = encodeURIComponent(name.trim());
  const cookieValue = `${encodeURIComponent(value.trim())}; expires=${exdate.toUTCString()}`;
  document.cookie = `${cookieName}=${cookieValue}; SameSite=lax; Secure`;
}

/**
 * Clears a cookie by its name by setting its expiration date to the past.
 *
 * @param name - The name of the cookie to clear.
 * @returns void
 */
export function clearCookie(name: string): void {
  const exdate = new Date();
  exdate.setDate(exdate.getDate() - 1);
  const cookieName = encodeURIComponent(name.trim());
  document.cookie = `${cookieName}=; expires=${exdate.toUTCString()}; SameSite=lax; Secure`;
  console.warn(`Cookie ${name} cleared`);
}
